import React, { useState } from 'react';
import { Card, Button, Input, Select, DatePicker, message, Space, Divider, Typography, Row, Col, Statistic, Modal, Descriptions, Tag, Table, Upload, Alert } from 'antd';
import type { UploadFile } from 'antd';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, type AllSettlementClientResponse } from './graphql/queries/getAllSettlementClient';
import { settlementClientRoleTypeOptions, SettlementClientRoleType, type SettlementClientRoleTypeValue } from './config/settlementClientRoleType';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { 
  CREATE_MOCK_FEE_ITEMS, 
  CREATE_MOCK_BILL_TYPE_FEE_ITEM_CONFIGS, 
  CREATE_MOCK_VOUCHER_CLEARINGS, 
  CLEANUP_TEST_DATA
} from '../../../graphql/mutations/settlement/settlementBillMutations';
import { CREATE_ASYNC_BILL_SUMMARY } from '../../../graphql/mutations/settlement/billSummaryMutations';
import { GET_BILL_DETAIL } from '../../../graphql/queries/settlement/getSettlementBillQueries';
import { BillSummaryProgressModal } from '../../../components/settlement/BillSummaryProgressModal';
import { renderBillTypeTag } from '../../../utils/billTypeConfig';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import dayjs from 'dayjs';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
  SYNC_ORDERS_TO_SETTLEMENT_ORDER,
  UPLOAD_ORDER_SORT_RESULT,
  TRIGGER_HOURLY_CYCLE_BILLING,
  TRIGGER_DSP_ADJUST_BILL_PHASE4,
} from '../../../graphql/mutations/settlement/cyclePricingTestMutations';
import {
  QUERY_CYCLE_BILLING_RESULTS,
  QUERY_SETTLEMENT_ORDERS,
} from '../../../graphql/queries/settlement/cyclePricingTestQueries';

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const BillingTestPage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const [clientId, setClientId] = useState<string | undefined>();
  const [clientRoleType, setClientRoleType] = useState<SettlementClientRoleTypeValue>(SettlementClientRoleType.CHANNEL_CLIENT);
  const [billType, setBillType] = useState<string>('DELIVERY_FEE_BILL');
  const [clearingCount, setClearingCount] = useState<number>(100);
  const [periodRange, setPeriodRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  
  // 账单汇总测试相关状态
  const [billIdForSummary, setBillIdForSummary] = useState<string>('');
  const [summaryProgressModalVisible, setSummaryProgressModalVisible] = useState(false);
  const [summaryTaskId, setSummaryTaskId] = useState<string | null>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [billDetailForConfirm, setBillDetailForConfirm] = useState<any>(null);
  
  // 测试结果状态
  const [testResults, setTestResults] = useState<{
    feeItemsCount: number;
    configsCount: number;
    clearingsCount: number;
  } | null>(null);

  // 周期性计费测试状态
  const [syncTrackingNos, setSyncTrackingNos] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [uploadFileBase64, setUploadFileBase64] = useState<{ base64: string; name: string } | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerPhase4Loading, setTriggerPhase4Loading] = useState(false);
  const [billingResultInput, setBillingResultInput] = useState('');
  const [billingResults, setBillingResults] = useState<any[]>([]);
  const [billingResultLoading, setBillingResultLoading] = useState(false);
  const [settlementOrderInput, setSettlementOrderInput] = useState('');
  const [settlementOrders, setSettlementOrders] = useState<any[]>([]);
  const [settlementOrderLoading, setSettlementOrderLoading] = useState(false);

  // Mutations and Queries
  const [createMockFeeItems] = useMutation(CREATE_MOCK_FEE_ITEMS);
  const [createMockBillTypeFeeItemConfigs] = useMutation(CREATE_MOCK_BILL_TYPE_FEE_ITEM_CONFIGS);
  const [createMockVoucherClearings] = useMutation(CREATE_MOCK_VOUCHER_CLEARINGS);
  const [cleanupTestData] = useMutation(CLEANUP_TEST_DATA);
  const [createAsyncBillSummary] = useMutation(CREATE_ASYNC_BILL_SUMMARY);
  const [getBillDetail, { loading: billDetailLoading }] = useLazyQuery(GET_BILL_DETAIL);
  const [syncOrdersMutation] = useMutation(SYNC_ORDERS_TO_SETTLEMENT_ORDER);
  const [uploadOrderSortResultMutation] = useMutation(UPLOAD_ORDER_SORT_RESULT);
  const [triggerHourlyMutation] = useMutation(TRIGGER_HOURLY_CYCLE_BILLING);
  const [triggerDspAdjustBillPhase4Mutation] = useMutation(TRIGGER_DSP_ADJUST_BILL_PHASE4);
  const [queryCycleBillingResults] = useLazyQuery(QUERY_CYCLE_BILLING_RESULTS);
  const [querySettlementOrdersLazy] = useLazyQuery(QUERY_SETTLEMENT_ORDERS);

  const { data: settlementClientData, loading: settlementClientLoading } = useQuery<AllSettlementClientResponse>(
    GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS,
    {
      variables: { roleType: clientRoleType },
      fetchPolicy: 'cache-and-network'
    }
  );

  const settlementClientOptions = settlementClientData?.allOptions || [];
  const clientLabel = clientRoleType === SettlementClientRoleType.DSP_CLIENT ? 'DSP' : '客户(channel)';

  // 处理创建Mock清分数据
  const handleCreateMockVoucherClearings = async () => {
    if (!clientId || !billType || !periodRange) {
      message.warning('请先选择客户、账单类型和时间范围');
      return;
    }

    try {
      const result = await createMockVoucherClearings({
        variables: {
          clientId: parseInt(clientId),
          billType: billType,
          count: clearingCount,
          periodStart: periodRange[0].toISOString(),
          periodEnd: periodRange[1].toISOString()
        }
      });

      if (result.data?.createMockVoucherClearings?.success) {
        message.success(result.data.createMockVoucherClearings.message);
        setTestResults(prev => ({
          ...prev!,
          clearingsCount: result.data.createMockVoucherClearings.dataCount
        }));
      } else {
        message.error(result.data?.createMockVoucherClearings?.message || '创建清分数据失败');
      }
    } catch (error) {
      message.error('创建清分数据失败: ' + (error as Error).message);
    }
  };

  // 处理清理测试数据
  const handleCleanupTestData = async () => {
    if (!clientId || !billType) {
      message.warning('请先选择客户和账单类型');
      return;
    }

    try {
      const result = await cleanupTestData({
        variables: {
          clientId: parseInt(clientId),
          billType: billType
        }
      });

      if (result.data?.cleanupTestData?.success) {
        message.success(result.data.cleanupTestData.message);
        setTestResults(null);
      } else {
        message.error(result.data?.cleanupTestData?.message || '清理测试数据失败');
      }
    } catch (error) {
      message.error('清理测试数据失败: ' + (error as Error).message);
    }
  };

  // 一键创建所有测试数据
  const handleCreateAllTestData = async () => {
    if (!clientId || !billType || !periodRange) {
      message.warning('请先选择客户、账单类型和时间范围');
      return;
    }

    try {
      // 1. 创建费用项
      const feeItemsResult = await createMockFeeItems({
        variables: {
          clientId: parseInt(clientId),
          billType: billType
        }
      });
      
      if (!feeItemsResult.data?.createMockFeeItems?.success) {
        throw new Error(feeItemsResult.data?.createMockFeeItems?.message || '创建费用项失败');
      }
      
      // 2. 创建账单类型费用项配置
      const configsResult = await createMockBillTypeFeeItemConfigs({
        variables: {
          clientId: parseInt(clientId),
          billType: billType
        }
      });
      
      if (!configsResult.data?.createMockBillTypeFeeItemConfigs?.success) {
        throw new Error(configsResult.data?.createMockBillTypeFeeItemConfigs?.message || '创建配置失败');
      }
      
      // 3. 创建清分数据
      await handleCreateMockVoucherClearings();
      
      message.success('所有测试数据创建完成！');
    } catch (error) {
      message.error('创建测试数据失败: ' + (error as Error).message);
    }
  };

  // 处理账单汇总测试 - 先查询账单信息
  const handleBillSummaryTest = async () => {
    if (!billIdForSummary) {
      message.warning('请输入账单ID');
      return;
    }

    try {
      // 先查询账单详情
      const result = await getBillDetail({
        variables: { billId: billIdForSummary }
      });

      if (result.data?.getBillDetail?.bill) {
        setBillDetailForConfirm(result.data.getBillDetail.bill);
        setConfirmModalVisible(true);
      } else {
        message.error('账单不存在或查询失败');
      }
    } catch (error) {
      console.error('查询账单详情失败:', error);
      message.error('查询账单详情失败: ' + (error as Error).message);
    }
  };

  // 确认执行汇总
  const handleConfirmSummary = async () => {
    try {
      const result = await createAsyncBillSummary({
        variables: { billId: billIdForSummary }
      });
      
      if (result.data?.createAsyncBillSummary?.success) {
        // 关闭确认模态框
        setConfirmModalVisible(false);
        setBillDetailForConfirm(null);
        
        // 设置任务ID并打开进度弹窗
        setSummaryTaskId(result.data.createAsyncBillSummary.taskId);
        setSummaryProgressModalVisible(true);
      } else {
        message.error(result.data?.createAsyncBillSummary?.message || '创建汇总任务失败');
      }
    } catch (error) {
      console.error('创建汇总任务失败:', error);
      message.error('创建汇总任务失败: ' + (error as Error).message);
    }
  };

  // 汇总任务完成回调
  const handleSummaryCompleted = (success: boolean) => {
    if (success) {
      message.success('账单汇总完成！');
    } else {
      message.error('账单汇总失败！');
    }
  };

  const parseLines = (text: string) =>
    text.split('\n').map(s => s.trim()).filter(Boolean);

  const handleDownloadCsvTemplate = () => {
    const content = '订单号,DSPID,分组ID,分组位置\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'order_sort_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSyncOrders = async () => {
    const trackingNos = parseLines(syncTrackingNos);
    if (trackingNos.length === 0) {
      message.warning('请输入运单号');
      return;
    }
    setSyncLoading(true);
    try {
      const result = await syncOrdersMutation({ variables: { trackingNos } });
      message.success(result.data?.syncOrdersToSettlementOrder || '同步成功');
    } catch (error) {
      message.error('同步失败: ' + (error as Error).message);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUploadOrderSort = async () => {
    if (!uploadFileBase64) {
      message.warning('请先选择 CSV 文件');
      return;
    }
    setUploadLoading(true);
    try {
      const result = await uploadOrderSortResultMutation({
        variables: { fileBase64: uploadFileBase64.base64, fileName: uploadFileBase64.name },
      });
      message.success(result.data?.uploadOrderSortResult || '上传成功');
      setUploadFileList([]);
      setUploadFileBase64(null);
    } catch (error) {
      message.error('上传失败: ' + (error as Error).message);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleTriggerHourly = async () => {
    setTriggerLoading(true);
    try {
      const result = await triggerHourlyMutation();
      message.success(result.data?.triggerHourlyCycleBilling || '触发成功');
    } catch (error) {
      message.error('触发失败: ' + (error as Error).message);
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleTriggerDspAdjustBillPhase4 = async () => {
    setTriggerPhase4Loading(true);
    try {
      const result = await triggerDspAdjustBillPhase4Mutation();
      const data = result.data?.triggerDspAdjustBillPhase4;
      if (data?.alreadyRunning) {
        message.warning('已有任务在执行中，请稍后重试');
      } else {
        message.success(`已触发，共 ${data?.taskCount ?? 0} 条任务`);
      }
    } catch (error) {
      message.error('触发失败: ' + (error as Error).message);
    } finally {
      setTriggerPhase4Loading(false);
    }
  };

  const handleQueryBillingResults = async () => {
    const trackingNos = parseLines(billingResultInput);
    if (trackingNos.length === 0) {
      message.warning('请输入运单号');
      return;
    }
    setBillingResultLoading(true);
    try {
      const result = await queryCycleBillingResults({ variables: { trackingNos } });
      setBillingResults(result.data?.queryCycleBillingResults ?? []);
    } catch (error) {
      message.error('查询失败: ' + (error as Error).message);
    } finally {
      setBillingResultLoading(false);
    }
  };

  const handleQuerySettlementOrders = async () => {
    const businessIds = parseLines(settlementOrderInput);
    if (businessIds.length === 0) {
      message.warning('请输入业务单号');
      return;
    }
    setSettlementOrderLoading(true);
    try {
      const result = await querySettlementOrdersLazy({ variables: { businessIds } });
      setSettlementOrders(result.data?.querySettlementOrders ?? []);
    } catch (error) {
      message.error('查询失败: ' + (error as Error).message);
    } finally {
      setSettlementOrderLoading(false);
    }
  };

  const billingResultColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '运单号', dataIndex: 'trackingNo', key: 'trackingNo', width: 160 },
    { title: '业务线', dataIndex: 'businessLine', key: 'businessLine', width: 100 },
    { title: '场景码', dataIndex: 'scenarioCode', key: 'scenarioCode', width: 120 },
    { title: '因子类型', dataIndex: 'factorType', key: 'factorType', width: 120 },
    { title: '使用状态', dataIndex: 'usageStatus', key: 'usageStatus', width: 100 },
    { title: '变更类型', dataIndex: 'changeType', key: 'changeType', width: 100 },
  ];

  const settlementOrderColumns = [
    { title: '订单ID', dataIndex: 'orderId', key: 'orderId', width: 100 },
    { title: '运单号', dataIndex: 'trackingNo', key: 'trackingNo', width: 160 },
    { title: '状态', dataIndex: 'overallStatus', key: 'overallStatus', width: 100 },
    { title: '客户ID', dataIndex: 'channelId', key: 'channelId', width: 90 },
    { title: 'DSP ID', dataIndex: 'dspId', key: 'dspId', width: 90 },
    { title: '三段码', dataIndex: 'orderSort', key: 'orderSort', width: 100 },
    { title: 'DSP运费(分)', dataIndex: 'dspAmount', key: 'dspAmount', width: 110 },
    { title: '同地址减免(分)', dataIndex: 'dspDiscountAmount', key: 'dspDiscountAmount', width: 120 },
    { title: '周期方案', dataIndex: 'feePrime', key: 'feePrime', width: 100 },
    { title: '分组ID', dataIndex: 'groupId', key: 'groupId', width: 120 },
    { title: '组内排序', dataIndex: 'groupSort', key: 'groupSort', width: 90 },
    { title: '任务日', dataIndex: 'taskDate', key: 'taskDate', width: 110 },
    { title: '妥投时间', dataIndex: 'deliveredAt', key: 'deliveredAt', width: 160 },
  ];

  return (
    <div className="p-6 space-y-6">
        <Card>
          <Title level={2}>账单出账测试环境</Title>
          <Text type="secondary">
            此页面用于测试账单出账和调账功能。可以创建Mock数据，执行账单汇总，然后测试调账功能。
          </Text>
        </Card>

        <Card title="测试配置">
          <Row gutter={16}>
            <Col span={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">客户类型</label>
                <Select
                  value={clientRoleType}
                  onChange={(value) => {
                    setClientRoleType(value);
                    setClientId(undefined);
                  }}
                  options={settlementClientRoleTypeOptions}
                  className="w-full"
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">{clientLabel}</label>
                <Select
                  value={clientId}
                  onChange={(val) => setClientId(val || undefined)}
                  className="w-full"
                  placeholder={`选择${clientLabel}`}
                  options={settlementClientOptions}
                  showSearch
                  optionFilterProp="label"
                  loading={settlementClientLoading}
                  allowClear
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">账单类型</label>
                <Select
                  value={billType}
                  onChange={setBillType}
                  className="w-full"
                >
                  <Option value="DELIVERY_FEE_BILL">运费账单</Option>
                  <Option value="SLA_BILL">SLA 账单</Option>
                  <Option value="DSP_DELIVERY_FEE_BILL">DSP运费账单</Option>
                  <Option value="SORTING_BILL">分拣账单</Option>
                </Select>
              </div>
            </Col>
            <Col span={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">清分数据数量</label>
                <Input
                  type="number"
                  value={clearingCount}
                  onChange={(e) => setClearingCount(parseInt(e.target.value) || 100)}
                  placeholder="100"
                />
              </div>
            </Col>
            <Col span={6}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">账期时间范围</label>
                <RangePicker
                  value={periodRange}
                  onChange={(dates) => setPeriodRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  className="w-full"
                  format="YYYY-MM-DD"
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="测试数据创建">
          <Space wrap>
            {canOperate && (
              <Button
                type="primary"
                onClick={handleCreateMockVoucherClearings}
                disabled={!clientId || !billType || !periodRange}
              >
                创建清分数据
              </Button>
            )}
            {canOperate && (
              <Button
                type="default"
                onClick={handleCreateAllTestData}
                disabled={!clientId || !billType || !periodRange}
              >
                一键创建所有数据
              </Button>
            )}
            {canOperate && (
              <Button
                danger
                onClick={handleCleanupTestData}
                disabled={!clientId || !billType}
              >
                清理测试数据
              </Button>
            )}
          </Space>
        </Card>

        {testResults && (
          <Card title="测试结果">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="清分数据数量" value={testResults.clearingsCount} />
              </Col>
            </Row>
          </Card>
        )}

        <Card title="账单汇总测试">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">账单ID</label>
              <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
                <Input
                  placeholder="输入账单ID"
                  value={billIdForSummary}
                  onChange={(e) => setBillIdForSummary(e.target.value)}
                  onPressEnter={handleBillSummaryTest}
                />
                {canOperate && (
                  <Button
                    type="primary"
                    onClick={handleBillSummaryTest}
                    disabled={!billIdForSummary}
                  >
                    执行汇总
                  </Button>
                )}
              </Space.Compact>
            </div>
            <Text type="secondary">
              输入账单ID后点击"执行汇总"按钮，系统将异步执行账单汇总任务，并实时显示进度。
            </Text>
          </div>
        </Card>

        <Card title="周期性计费测试">
          <div className="space-y-6">

            {/* 功能1：同步订单到结算订单宽表 */}
            <div>
              <Title level={5}>1. 同步订单到结算订单宽表</Title>
              <Alert
                message="仅非生产环境可用"
                type="warning"
                showIcon
                className="mb-3"
              />
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">运单号（每行一个）</label>
                <Input.TextArea
                  rows={4}
                  placeholder="每行输入一个运单号"
                  value={syncTrackingNos}
                  onChange={e => setSyncTrackingNos(e.target.value)}
                />
              </div>
              {canOperate && (
                <Button
                  type="primary"
                  loading={syncLoading}
                  onClick={handleSyncOrders}
                >
                  同步订单
                </Button>
              )}
            </div>

            <Divider />

            {/* 功能2：上传订单分组排序结果 */}
            <div>
              <Title level={5}>2. 上传订单分组排序结果并触发每日计费</Title>
              <Alert
                message="仅非生产环境可用"
                type="warning"
                showIcon
                className="mb-3"
              />
              <Space className="mb-3">
                <Button onClick={handleDownloadCsvTemplate}>下载 CSV 模板</Button>
                <Text type="secondary">列：订单号 / DSPID / 分组ID / 分组位置</Text>
              </Space>
              <div className="mb-2">
                <Upload
                  accept=".csv"
                  fileList={uploadFileList}
                  beforeUpload={file => {
                    const reader = new FileReader();
                    reader.onload = e => {
                      const base64 = (e.target?.result as string).split(',')[1];
                      setUploadFileBase64({ base64, name: file.name });
                    };
                    reader.readAsDataURL(file);
                    setUploadFileList([{ uid: '-1', name: file.name, status: 'done' }]);
                    return false;
                  }}
                  onRemove={() => {
                    setUploadFileList([]);
                    setUploadFileBase64(null);
                  }}
                  maxCount={1}
                >
                  <Button>选择 CSV 文件</Button>
                </Upload>
              </div>
              {canOperate && (
                <Button
                  type="primary"
                  loading={uploadLoading}
                  onClick={handleUploadOrderSort}
                  disabled={!uploadFileBase64}
                >
                  上传并同步到周期性计费变更池
                </Button>
              )}
            </div>

            <Divider />

            {/* 功能3：触发周期性计费 */}
            <div>
              <Title level={5}>3. 触发周期性计费（按小时）</Title>
              {canOperate && (
                <Button
                  type="primary"
                  loading={triggerLoading}
                  onClick={handleTriggerHourly}
                >
                  触发周期性计费
                </Button>
              )}
            </div>

            <Divider />

            {/* 功能4：查询周期性计费变更池结果 */}
            <div>
              <Title level={5}>4. 查询周期性计费变更池结果</Title>
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">运单号（每行一个）</label>
                <Input.TextArea
                  rows={4}
                  placeholder="每行输入一个运单号"
                  value={billingResultInput}
                  onChange={e => setBillingResultInput(e.target.value)}
                />
              </div>
              <Button
                type="primary"
                loading={billingResultLoading}
                onClick={handleQueryBillingResults}
                className="mb-3"
              >
                查询
              </Button>
              <Table
                dataSource={billingResults}
                columns={billingResultColumns}
                rowKey={(r, i) => r.id ?? i!}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 20, showTotal: total => `共 ${total} 条` }}
              />
            </div>

            <Divider />

            {/* 功能5：查询结算订单宽表 */}
            <div>
              <Title level={5}>5. 查询结算订单宽表</Title>
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">业务单号（每行一个）</label>
                <Input.TextArea
                  rows={4}
                  placeholder="每行输入一个业务单号（运单号）"
                  value={settlementOrderInput}
                  onChange={e => setSettlementOrderInput(e.target.value)}
                />
              </div>
              <Button
                type="primary"
                loading={settlementOrderLoading}
                onClick={handleQuerySettlementOrders}
                className="mb-3"
              >
                查询
              </Button>
              <Table
                dataSource={settlementOrders}
                columns={settlementOrderColumns}
                rowKey={(r, i) => r.orderId ?? i!}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 20, showTotal: total => `共 ${total} 条` }}
              />
            </div>

            <Divider />

            {/* 功能6：触发Dsp明细调账第4阶段同步定时任务 */}
            <div>
              <Title level={5}>6. 触发 Dsp 明细调账第4阶段同步定时任务</Title>
              {canOperate && (
                <Button
                  type="primary"
                  loading={triggerPhase4Loading}
                  onClick={handleTriggerDspAdjustBillPhase4}
                >
                  触发第4阶段同步
                </Button>
              )}
            </div>

          </div>
        </Card>

        <Card title="测试说明">
          <div className="space-y-2">
            <Text><strong>测试流程：</strong></Text>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>选择客户和账单类型</li>
              <li>设置账期时间范围</li>
              <li>点击"一键创建所有数据"创建测试数据（包括费用项、费用项配置和清分数据）</li>
              <li>前往"账期管理"页面查看账期实例和账单，获取账单ID</li>
              <li>在"账单汇总测试"区域输入账单ID，点击"执行汇总"测试汇总功能</li>
              <li>在"结算账单"页面测试调账、导出等功能</li>
              <li>测试完成后点击"清理测试数据"</li>
            </ol>
            <Divider />
            <Text type="secondary">
              <strong>注意事项：</strong>
              <br />
              • 测试数据会创建真实的费用项、配置和清分数据
              <br />
              • 请确保选择的客户ID存在，且已配置账期规则
              <br />
              • 测试完成后请及时清理数据，避免影响生产环境
            </Text>
          </div>
        </Card>

        {/* 账单信息确认模态框 */}
        <Modal
          title="确认执行账单汇总"
          open={confirmModalVisible}
          onCancel={() => {
            setConfirmModalVisible(false);
            setBillDetailForConfirm(null);
          }}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => {
                setConfirmModalVisible(false);
                setBillDetailForConfirm(null);
              }}
            >
              取消
            </Button>,
            <Button
              key="confirm"
              type="primary"
              onClick={handleConfirmSummary}
              disabled={billDetailForConfirm?.isCanceled || billDetailForConfirm?.status === 'INTERNAL_CONFIRMED'}
            >
              确认汇总
            </Button>
          ]}
          width={800}
        >
          {billDetailForConfirm && (
            <div className="space-y-4">
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="账单ID">{billDetailForConfirm.id}</Descriptions.Item>
                <Descriptions.Item label="客户名称">{billDetailForConfirm.clientName}</Descriptions.Item>
                <Descriptions.Item label="客户ID">{billDetailForConfirm.clientId}</Descriptions.Item>
                <Descriptions.Item label="账单类型">
                  {renderBillTypeTag(billDetailForConfirm.billType)}
                </Descriptions.Item>
                <Descriptions.Item label="账单状态">
                  {billDetailForConfirm.isCanceled ? (
                    <Tag color="red">账单已作废</Tag>
                  ) : (
                    <Tag color={
                      billDetailForConfirm.status === 'PRE_GENERATED' ? 'default' :
                      billDetailForConfirm.status === 'PENDING_SUMMARY' ? 'blue' :
                      billDetailForConfirm.status === 'INTERNAL_ALREADY' ? 'orange' :
                      billDetailForConfirm.status === 'ADJUSTMENT_PENDING' ? 'purple' :
                      billDetailForConfirm.status === 'INTERNAL_CONFIRMED' ? 'green' : 'default'
                    }>
                      {billDetailForConfirm.status === 'PRE_GENERATED' ? '账单初始化' :
                       billDetailForConfirm.status === 'PENDING_SUMMARY' ? '账单汇总待出账' :
                       billDetailForConfirm.status === 'INTERNAL_ALREADY' ? '账单已出账' :
                       billDetailForConfirm.status === 'ADJUSTMENT_PENDING' ? '账单调账待确认' :
                       billDetailForConfirm.status === 'INTERNAL_CONFIRMED' ? '账单已内部确认' : billDetailForConfirm.status}
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="账单金额">
                  <span className="text-lg font-bold text-red-600">{billDetailForConfirm.billAmount}</span>
                </Descriptions.Item>
                <Descriptions.Item label="账单周期" span={2}>
                  {formatTimeWithTimezone(billDetailForConfirm.periodStart, billDetailForConfirm.timeZone)} ~ {formatTimeWithTimezone(billDetailForConfirm.periodEnd, billDetailForConfirm.timeZone)}
                </Descriptions.Item>
                <Descriptions.Item label="时区">{billDetailForConfirm.timeZone}</Descriptions.Item>
                <Descriptions.Item label="币种">{billDetailForConfirm.currency}</Descriptions.Item>
                <Descriptions.Item label="创建时间" span={2}>
                  {new Date(billDetailForConfirm.createdAt).toLocaleString('zh-CN')}
                </Descriptions.Item>
              </Descriptions>

              {billDetailForConfirm.isCanceled && (
                <div className="bg-red-50 p-3 rounded text-sm text-red-800">
                  <strong>警告：</strong>该账单已作废，无法执行汇总操作。
                </div>
              )}

              {billDetailForConfirm.status === 'INTERNAL_CONFIRMED' && (
                <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800">
                  <strong>提示：</strong>该账单已确认，无法执行汇总操作。
                </div>
              )}

              {!billDetailForConfirm.isCanceled && billDetailForConfirm.status !== 'INTERNAL_CONFIRMED' && (
                <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                  <div className="font-medium mb-1">汇总说明：</div>
                  <ul className="list-disc list-inside space-y-1">
                    <li>系统将异步执行账单汇总任务</li>
                    <li>汇总过程会关联清分数据并计算费用项金额</li>
                    <li>汇总完成后账单状态将更新</li>
                    <li>可通过进度弹窗实时查看汇总进度</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* 账单汇总进度弹窗 */}
        <BillSummaryProgressModal
          taskId={summaryTaskId}
          visible={summaryProgressModalVisible}
          onClose={() => {
            setSummaryProgressModalVisible(false);
            setSummaryTaskId(null);
          }}
          onCompleted={handleSummaryCompleted}
        />
    </div>
  );
};

export default BillingTestPage;
